import { DOCUMENT } from '@angular/common';
import {
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Inject,
  OnDestroy,
  Output,
} from '@angular/core';

const RESIZE_HIT_AREA = 12;
const BOUNDARY_ALIGNMENT_TOLERANCE = 1;

export interface ResizableColumnResizeEvent {
  columnId: string;
  width: number;
}

export interface ResizableColumnStartEvent extends ResizableColumnResizeEvent {
  columnElement: HTMLElement;
}

export interface ResizableColumnEndEvent extends ResizableColumnResizeEvent {
  cancelled: boolean;
}

interface ColumnBoundary {
  columnCell: HTMLElement;
  columnId: string;
}

@Directive({
  selector: 'table[resizableColumns]',
  standalone: false,
})
export class ResizableColumnsDirective implements OnDestroy {
  @Output()
  readonly resizableColumnHover = new EventEmitter<string | null>();

  @Output()
  readonly resizableColumnStart = new EventEmitter<ResizableColumnStartEvent>();

  @Output()
  readonly resizableColumn = new EventEmitter<ResizableColumnResizeEvent>();

  @Output()
  readonly resizableColumnEnd = new EventEmitter<ResizableColumnEndEvent>();

  @HostBinding('class.resize-boundary-hovered')
  protected resizeBoundaryHovered = false;

  private activeBoundary: ColumnBoundary | null = null;
  private currentWidth = 0;
  private hoveredColumnId: string | null = null;
  private pointerId: number | null = null;
  private previousBodyCursor = '';
  private previousBodyUserSelect = '';
  private startClientX = 0;
  private startWidth = 0;
  private suppressNextClick = false;

  constructor(
    @Inject(DOCUMENT) private readonly documentRef: Document,
    private readonly elementRef: ElementRef<HTMLTableElement>,
  ) {}

  @HostListener('pointermove', ['$event'])
  handlePointerMove(event: PointerEvent): void {
    if (event.pointerId === this.pointerId && this.activeBoundary) {
      event.preventDefault();
      const width = Math.round(this.startWidth + event.clientX - this.startClientX);
      if (width === this.currentWidth) {
        return;
      }

      this.currentWidth = width;
      this.resizableColumn.emit({ columnId: this.activeBoundary.columnId, width });
      return;
    }

    if (this.pointerId === null) {
      this.setHoveredColumn(this.resolveBoundary(event)?.columnId ?? null);
    }
  }

  @HostListener('pointerdown', ['$event'])
  handlePointerDown(event: PointerEvent): void {
    if (event.button !== 0 || this.pointerId !== null) {
      return;
    }

    const boundary = this.resolveBoundary(event);
    const startWidth = Math.round(boundary?.columnCell.getBoundingClientRect().width ?? 0);
    if (!boundary || !Number.isFinite(startWidth) || startWidth <= 0) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    try {
      this.elementRef.nativeElement.setPointerCapture(event.pointerId);
    } catch {
      return;
    }

    this.activeBoundary = boundary;
    this.pointerId = event.pointerId;
    this.startClientX = event.clientX;
    this.startWidth = startWidth;
    this.currentWidth = startWidth;
    this.suppressNextClick = true;
    this.previousBodyCursor = this.documentRef.body.style.cursor;
    this.previousBodyUserSelect = this.documentRef.body.style.userSelect;
    this.documentRef.body.style.cursor = 'col-resize';
    this.documentRef.body.style.userSelect = 'none';
    this.setHoveredColumn(boundary.columnId);

    this.resizableColumnStart.emit({
      columnElement: boundary.columnCell,
      columnId: boundary.columnId,
      width: startWidth,
    });
  }

  @HostListener('pointerup', ['$event'])
  handlePointerUp(event: PointerEvent): void {
    if (event.pointerId === this.pointerId) {
      this.finishResize(false);
    }
  }

  @HostListener('pointercancel', ['$event'])
  handlePointerCancel(event: PointerEvent): void {
    if (event.pointerId === this.pointerId) {
      this.finishResize(true);
    }
  }

  @HostListener('lostpointercapture', ['$event'])
  handleLostPointerCapture(event: PointerEvent): void {
    if (event.pointerId === this.pointerId) {
      this.finishResize(false);
    }
  }

  @HostListener('pointerleave')
  handlePointerLeave(): void {
    if (this.pointerId === null) {
      this.setHoveredColumn(null);
    }
  }

  @HostListener('click', ['$event'])
  handleClick(event: MouseEvent): void {
    if (!this.suppressNextClick) {
      return;
    }

    this.suppressNextClick = false;
    event.preventDefault();
    event.stopPropagation();
  }

  ngOnDestroy(): void {
    this.clearPointerState();
    this.setHoveredColumn(null);
  }

  private resolveBoundary(event: PointerEvent): ColumnBoundary | null {
    const target = event.target;
    const table = this.elementRef.nativeElement;
    const cell = target instanceof Element ? target.closest<HTMLElement>('th, td') : null;
    if (!cell || cell.closest('table') !== table) {
      return null;
    }

    const bounds = cell.getBoundingClientRect();
    const columnId = cell.dataset.resizableColumnId;
    const distanceFromRight = Math.abs(event.clientX - bounds.right);
    const rightBoundary =
      columnId && distanceFromRight <= RESIZE_HIT_AREA
        ? {
            columnCell: cell,
            columnId,
          }
        : null;

    const distanceFromLeft = Math.abs(event.clientX - bounds.left);
    if (distanceFromLeft > RESIZE_HIT_AREA) {
      return rightBoundary;
    }

    const previousCell = cell.previousElementSibling as HTMLElement | null;
    const previousColumnId = previousCell?.dataset.resizableColumnId;
    const previousCellRight = previousCell?.getBoundingClientRect().right;
    const cellsShareBoundary =
      previousCellRight !== undefined && Math.abs(previousCellRight - bounds.left) <= BOUNDARY_ALIGNMENT_TOLERANCE;
    if (
      previousCell &&
      previousColumnId &&
      cellsShareBoundary &&
      (!rightBoundary || distanceFromLeft < distanceFromRight)
    ) {
      return {
        columnCell: previousCell,
        columnId: previousColumnId,
      };
    }

    return rightBoundary;
  }

  private finishResize(cancelled: boolean): void {
    const activeBoundary = this.activeBoundary;
    const width = this.currentWidth;
    this.clearPointerState();
    this.setHoveredColumn(null);

    if (cancelled) {
      this.suppressNextClick = false;
    }

    if (activeBoundary) {
      this.resizableColumnEnd.emit({ cancelled, columnId: activeBoundary.columnId, width });
    }
  }

  private clearPointerState(): void {
    const pointerId = this.pointerId;
    this.activeBoundary = null;
    this.pointerId = null;

    if (pointerId !== null && this.elementRef.nativeElement.hasPointerCapture(pointerId)) {
      this.elementRef.nativeElement.releasePointerCapture(pointerId);
    }

    if (pointerId !== null) {
      this.documentRef.body.style.cursor = this.previousBodyCursor;
      this.documentRef.body.style.userSelect = this.previousBodyUserSelect;
    }
  }

  private setHoveredColumn(columnId: string | null): void {
    if (this.hoveredColumnId === columnId) {
      return;
    }

    this.hoveredColumnId = columnId;
    this.resizeBoundaryHovered = columnId !== null;
    this.resizableColumnHover.emit(columnId);
  }
}
